import { computed, makeObservable, observable, action, runInAction } from "mobx"
import loadingElement from './loadingElement'
import { CharacterStore } from "../characterstore/CharacterStore";
import { resetQueryByParams } from "./utils";
import { PageCache } from "./PageCache"
import { Result } from "./Result";

export const PageStatusType = {
  INIT: 'init',
  EDIT: 'edit',
  BROWSE: 'browse',
  ADD: 'add'
}
// define interface4props
interface PageStoreProps {
  status?: string, // page status
  query?: object, // router
  params?: object, // bill params
  enumData?: object,
  userDefines?: object,
  multiData?: object,
  history?: object,
  bRunInDesigner?: boolean,
  rootStore?: any
}
export class PageStore {
  storeType: string = 'PageStore'
  // 全局变量：控制是否显示 loading 提示框
  loadingCount: number;
  pendingActionCount: number;
  /**
   * 全局变量：当前页面的显示模式
   * init - 初始化模式
   * add - 新增模式
   * edit - 编辑模式
   * browse - 浏览模式
   */
  status: string;
  /**
   * query.terminalType 终端类型
   * 1 - PC 端
   * 2 - 触屏端（PAD，electron 桌面）
   * 3 - 移动端
   *
   * query.domainKey 领域编码
   * query.serviceCode 原子服务编码，工作台去 iframe 渲染时将该字段值作为当前页面渲染的 ROOT DOM NODE。
   * query.busiObj 业务对象编码
   */
  query: object = {};
  params: object = {};
  enumData: object = {};
  userDefines: object = {};
  multiData: object = {};
  characterStore: CharacterStore
  history: any = {};
  // 全局变量：控制卡片返回列表时是否需要刷新数据
  bNeedRefresh: boolean = false;
  pageRefresh: string = new Date().toString()
  /**
   * 页签内打开、关闭、刷新单据
   * 说明：如果租户级配置了newOpenTabs开关特性，则说明打开卡片等详情页为通过工作台页签打开，push方法内部调用openService API，业务代码无需敏感。
   */
  push: () => any;
  goBack: () => any;
  refresh: () => any;
  showPage: (pageCode: string) => void;
  closePage: () => void;
  /**
   * 弹框方式打开或关闭单据
   */
  openModal: () => any;
  // 全局变量：当前运行环境是在设计态还是运行态，默认 false 为运行态，置为 true 则为设计态
  bRunInDesigner: boolean = false;
  // 移动端打开子页面的栈
  pageStack: Array<String>;

  // 页面缓存数据对象
  pageCache: PageCache;

  rootStore: any = {};

  result: Result

  constructor(props: PageStoreProps = {}) {
    const { status, query, params, enumData, userDefines, multiData, history, bRunInDesigner = false, rootStore } = props;
    const mode = [params?.mode, query?.mode, params?.billid, query?.billid].find(v => Object.values(PageStatusType).includes(v));
    // @ts-ignore
    const historyParams = history?.getHistoryParams?.() || {};
    this.status = historyParams.status ? historyParams.status : (status || mode || PageStatusType.BROWSE);
    this.query = Object.assign(this.query, query, historyParams.query)
    this.params = Object.assign(this.params, params, historyParams.params)
    // pageStore里的params中有store对象，不应该有，暂时先这样处理
    if (this.params.store) delete this.params.store
    resetQueryByParams(this.query, this.params);
    this.enumData = Object.assign(this.enumData, enumData)
    this.multiData = multiData || {}
    this.userDefines = Object.assign(this.userDefines, userDefines)
    this.characterStore = new CharacterStore()
    this.bRunInDesigner = bRunInDesigner
    // 不对外暴露使用。
    this.history = Object.assign(this.history, history);
    this.pageStack = []
    // 支持参照渲染
    this.rootStore = rootStore

    this.loadingCount = 0;
    // 骨架屏需要等待的异步任务计数（计算清零后才隐藏骨架屏）
    this.pendingActionCount = -1;
    this.pageCache = new PageCache()
    this.result = new Result()
    makeObservable(this, {
      bRunInDesigner: observable,
      status: observable,
      loadingCount: observable,
      pendingActionCount: observable,
      bNeedRefresh: observable,
      pageRefresh: observable,
      pageCache: observable,
      enumData: observable,
      isEdit: computed,
      isInit: computed,
      isBrowse: computed,
      hasResume: computed,
      edit: action,
      browse: action,
      getStatus: action,
      setStatus: action,
      startLoading: action,
      endLoading: action,
      updatePendingActionCount: action,
      setResult: action,
      getLoadingCount: action,
      pageStack: observable,
      currentChildPage: computed
    })
    this.push = (...props) => {
      props[0].params ? null : props[0].params = {}
      props[0].params.store = this
      this.history.push?.(...props)
    }
    this.goBack = (...props) => {
      props[0] ? props[0].sourcePage = this.params.sourcePage : null;
      return this.history.goBack(...props)
    }
    this.openModal = (...props) => {
      props[0].params ? null : props[0].params = {}
      props[0].params.store = this
      this.history.openModal(...props)
    }
    this.closeModal = (...props) => {
      this.history.closeModal(...props)
    }
    this.refresh = (...props) => {
      this.history.refresh(...props)
    }
    this.showPage = (pageCode: string) => {
      if (this.currentChildPage !== pageCode) this.pageStack.push(pageCode)
    }
    this.closePage = () => {
      if (this.currentChildPage) this.pageStack.pop()
    }
    // mount loadingElements
    let loading = new loadingElement();
    loading.addLoading(this);
  }

  updatePendingActionCount (delta: number) {
    const prev = this.pendingActionCount === -1 ? 0 : this.pendingActionCount
    this.pendingActionCount = prev + delta
  }

  // --------------------------------- State Actions ---------------------------------
  add() {
    this.status = PageStatusType.ADD;
  }

  edit() {
    this.status = PageStatusType.EDIT;
  }

  browse() {
    this.status = PageStatusType.BROWSE;
  }

  getStatus() {
    return this.status;
  }

  setStatus(value: string) {
    this.status = value
  }

  get isInit() {
    return this.status === PageStatusType.INIT;
  }

  get isEdit() {
    return this.status === PageStatusType.EDIT;
  }

  get isBrowse() {
    return this.status === PageStatusType.BROWSE;
  }

  get currentChildPage() {
    if (this.pageStack.length) {
      return this.pageStack[this.pageStack.length - 1]
    } else {
      return ''
    }
  }

  get generalSetting() {
    try {
      let data = JSON.parse(window?.jDiwork?.diworkContext()?.generalSetting || '{}')
      // "{"tabOpenMode":0,"saveClose":false,"autoOpenApproval":true}"
      if(typeof data.tabOpenMode == 'undefined') data.tabOpenMode = 0
      return data
    } catch (err) {
      return {};
    }
  }
  get hasResume () {
    return this.result.resume
  }

  // --------------------------------- loading Actions ---------------------------------
  startLoading = () => {
    this.loadingCount += 1;
    return this.loadingCount;
  };

  endLoading = () => {
    this.loadingCount -= 1;
    return this.loadingCount;
  }

  getLoadingCount = () => {
    return this.loadingCount;
  }
  setResult = (param: any = {})=> {
    this.result.setData(param)
    // 兼容原来的逻辑 自动合并一下params
    this.params = Object.assign({}, this.params, param)
  }
  getResult = ()=> {
    return this.result.getResult()
  }
}
